import type { QueryClient } from "@tanstack/react-query";
import type { ParentData } from "features/cases/blocks/CommonCard/CommonCard.types";
import { buildShortLink } from "features/links/utils/shortLink";
import { getRestrictionErrorMessage } from "features/restrictions/utils";

// Общий POST для создания/редактирования проблемы/решения/карточки — используется
// и явным сохранением (useCardSave), и автосохранением (useAutosave), чтобы оба
// пути гарантированно обращались к API одинаково.
export const postCardContent = async (
  url: string,
  payload: Record<string, unknown>,
): Promise<any> => {
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    credentials: "include",
    // Позволяет запросу, отправленному из beforeunload/pagehide (автосохранение
    // перед закрытием вкладки), пережить переход со страницы, а не оборваться.
    keepalive: true,
  });

  const data = await response.json();

  if (!response.ok) {
    const restrictionMessage = getRestrictionErrorMessage(data);

    throw new Error(
      restrictionMessage || data?.error || `HTTP ${response.status}`,
    );
  }

  return data;
};

export const getCardShareUrl = (
  parentData: ParentData,
  cardContentId: string,
): string | undefined => {
  if (parentData.isProblem) {
    return buildShortLink({ kind: "problem", id: cardContentId });
  }

  if (parentData.isSolution) {
    return buildShortLink({
      kind: "solution",
      id: cardContentId,
      problemId: parentData.problemId,
    });
  }

  if (parentData.isAttribute) {
    return buildShortLink({
      kind: "card",
      id: cardContentId,
      problemId: parentData.problemId,
      solutionId:
        parentData.attributeOf === "solution"
          ? parentData.solutionId
          : undefined,
    });
  }

  return undefined;
};

export const getEditContentUrlAndQueryId = (
  isProblem: boolean,
  isSolution: boolean,
  isAttribute: boolean,
  cardContentId: string | undefined,
  problemId: string | undefined,
  apiHost: string,
): { editContentUrl: string; queryId: string | undefined } => {
  if (isProblem) {
    return {
      editContentUrl: cardContentId
        ? `${apiHost}/solutions/api/problems/${cardContentId}/edit/`
        : `${apiHost}/solutions/api/problems/add/`,
      queryId: `problems/${cardContentId}`,
    };
  }
  if (isSolution) {
    return {
      editContentUrl: cardContentId
        ? `${apiHost}/solutions/api/solutions/${cardContentId}/edit/`
        : `${apiHost}/solutions/api/problems/${problemId}/solutions/add/`,
      queryId: `problems/${problemId}/solutions/`,
    };
  }
  if (isAttribute) {
    return {
      editContentUrl: cardContentId
        ? `${apiHost}/solutions/api/cards/${cardContentId}/edit/`
        : `${apiHost}/solutions/api/cards/add/`,
      queryId: undefined,
    };
  }

  return { editContentUrl: "", queryId: undefined };
};

type AttributeVariantLike = { id: string; title: string };

export const getVariantMeta = (
  attributeVariant: string | AttributeVariantLike | undefined,
): { variantString: string; variantId: number | null } => {
  const variantString = String(
    typeof attributeVariant === "string"
      ? attributeVariant
      : attributeVariant?.title || "",
  );
  const variantId =
    Number(
      typeof attributeVariant === "string" ? null : attributeVariant?.id,
    ) || null;

  return { variantString, variantId };
};

export const getCardDimensions = ({
  mode,
  isMobile,
  vmode,
  expanded,
  embedded,
  baseHeight,
}: {
  mode: string;
  isMobile: boolean;
  vmode: string;
  expanded: boolean;
  embedded: boolean;
  baseHeight: number;
}): {
  width: string | number;
  minWidth?: number;
  height: string;
  minHeight: string;
} => {
  let width: string | number;
  let minWidth: number | undefined;

  switch (mode) {
    case "half":
      width = "40%";
      break;
    case "quarter":
      width = "20%";
      break;
    case "wide":
      width = "100%";
      break;
    case "default":
    default:
      width = isMobile ? 280 : 330;
      minWidth = isMobile ? 280 : 330;
      break;
  }

  let height =
    vmode === "short" ? "100%" : expanded ? "auto" : `${baseHeight}px`;

  if (embedded) {
    height = "100%";
    width = "100%";
  }

  const minHeight = vmode === "short" ? "unset" : `${baseHeight}px`;

  return { width, minWidth, height, minHeight };
};

export const getHasButtons = ({
  canComment,
  desktopOrNewCard,
  embedded,
  showComments,
  url,
  isEditor,
  editUrl,
  addUrl,
  showBottomToggle,
  canEdit,
  showEdit,
  isEditing,
}: {
  canComment: boolean;
  desktopOrNewCard: boolean;
  embedded: boolean;
  showComments: boolean;
  url: string | undefined;
  isEditor: boolean;
  editUrl: string | undefined;
  addUrl: string | undefined;
  showBottomToggle: boolean;
  canEdit: boolean;
  showEdit: boolean;
  isEditing: boolean;
}): boolean =>
  !!(
    (canComment && desktopOrNewCard && !embedded) ||
    showComments ||
    !!url ||
    (isEditor && editUrl) ||
    (isEditor && addUrl) ||
    showBottomToggle ||
    canEdit ||
    showEdit
  ) &&
  (!embedded || desktopOrNewCard || isEditing);

export const getCardClassName = (
  isModeratedCurrent: boolean,
  isDeletedCurrent: boolean,
  embedded: boolean,
  halfTransparent: boolean,
  isNewCard: boolean | string | null | undefined,
): string | undefined =>
  [
    isModeratedCurrent && !embedded ? "card-moderated" : "",
    isDeletedCurrent && !embedded ? "card-deleted" : "",
    halfTransparent ? "card-half-transparent" : "",
    isNewCard ? "card-clickable" : "",
  ]
    .filter(Boolean)
    .join(" ") || undefined;

export const isHalfTransparent = (
  isDraft: boolean,
  isModeratedCurrent: boolean,
  isDeletedCurrent: boolean,
  embedded: boolean,
): boolean => (isDraft || isModeratedCurrent || isDeletedCurrent) && !embedded;

export const removeHtmlTags = (text: string): string => {
  return text.replace(/<[^>]*>/g, "");
};

export const getQid = (
  parentData: ParentData,
  queryId: string | undefined,
): string | undefined => {
  if (parentData.fromCatalog) return "problems";

  if (!parentData.isAttribute) return queryId;

  switch (parentData.attributeOf) {
    case "problem":
      return `problems/${parentData.problemId}/cards/`;
    default:
      return `problems/${parentData.problemId}/solutions/${parentData.solutionId}/cards/`;
  }
};

// Проблема кешируется как один объект, решения/карточки — как список; этим
// пользуются и явное сохранение (useCardSave), и автосохранение (useAutosave),
// поэтому логика вынесена сюда, а не повторена в двух местах.
export const applySavedItemToCache = (
  queryClient: QueryClient,
  parentData: ParentData,
  queryId: string | undefined,
  updatedItem: any,
): void => {
  const qid = getQid(parentData, queryId);

  if (parentData.isProblem) {
    queryClient.setQueryData([qid], updatedItem);

    return;
  }

  queryClient.setQueryData([qid], (oldItems: any[] | undefined) => {
    if (!oldItems) return [];
    if (
      oldItems?.find &&
      !oldItems.find(
        (item) => item?.id && updatedItem?.id && item?.id === updatedItem?.id,
      )
    ) {
      return [updatedItem, ...oldItems];
    }

    return oldItems.map((item) =>
      item.id === updatedItem.id ? updatedItem : item,
    );
  });
};
